// @ts-check
import React, {
	createContext,
	useContext,
	useRef,
	useMemo,
	useCallback,
	useState,
	useEffect,
} from 'react';
import { useAuth } from 'react-oidc-context';
import useAuthenticated from './useAuthenticated';

/** @typedef {(options?: {signal?: AbortSignal}) => Promise<import('oidc-client-ts').User | null>} SilentRefreshFn */
/** @typedef {[boolean, SilentRefreshFn]} SilentContextI  */
/** @type {React.Context<SilentContextI>} */
const silentContext = createContext(/** @type {*} */ (null));

export function useOidcSilent() {
	return useContext(silentContext);
}

/**
 * Silent signin provider provides loading feedback on the initial silent signin
 * @param {Object} props
 * @param {*} props.children
 */
export function OidcSilentProvider({ children }) {
	const [isSilentLoading, setSilentLoading] = useState(true);
	const { isLoading, signinSilent } = useAuth();
	const isAuthenticated = useAuthenticated();

	const refreshPromise = useRef(
		/** @type ReturnType<signinSilent> | undefined */ (undefined),
	);

	/** @type SilentRefreshFn */
	const silentRefresh = useCallback(
		async ({ signal } = {}) => {
			if (signal?.aborted) return null;

			// Group silentRefresh calls
			if (refreshPromise.current) return refreshPromise.current;

			const promise = signinSilent();
			refreshPromise.current = promise;

			// Clean up only once
			let isClean = false;
			const cleanup = () => {
				if (isClean) return;
				isClean = true;
				refreshPromise.current = undefined;
				setSilentLoading(false);
			};

			signal?.addEventListener('abort', cleanup);

			// Clean up states
			promise.catch(() => null).then(cleanup);

			return promise;
		},
		[signinSilent],
	);

	// Try a silent signin on load
	const silentStatus = useRef(false);
	useEffect(() => {
		const controller = new AbortController();

		if (isLoading) return undefined;

		// Call it just once
		if (silentStatus.current) return undefined;
		silentStatus.current = true;

		// Skip all together if we good
		if (isAuthenticated) {
			setSilentLoading(false);
			return undefined;
		}

		silentRefresh({ signal: controller.signal });

		return () => {
			controller.abort();
		};
	}, [isLoading, isAuthenticated, silentRefresh]);

	/** @type SilentContextI */
	const value = useMemo(
		() => [isSilentLoading, silentRefresh],
		[isSilentLoading, silentRefresh],
	);

	return (
		<silentContext.Provider value={value}>{children}</silentContext.Provider>
	);
}

export default OidcSilentProvider;
